export interface CoviSummaryPreferenceSelectors {
  agents: string[]
  metadataKeys: string[]
  metrics: string[]
  personas: string[]
  tags: string[]
  terms: string[]
  testSets: string[]
}

export interface CoviSummaryMemory {
  memoryEnabled: boolean | null
  preferencesText: string
  scope: "org"
  selectors: CoviSummaryPreferenceSelectors
  updatedAt: string | null
}

interface CoviSummaryMemoryChangeDetail {
  memory?: CoviSummaryMemory
  orgSlug?: string
  refresh?: boolean
}

const SUMMARY_MEMORY_STORAGE_KEY_PREFIX = "coval:copilot:summary-memory"
const LEGACY_MONITORING_FOCUS_STORAGE_KEY_PREFIX = "monitoring-summary-focus"
const SUMMARY_MEMORY_EVENT = "coval:covi-summary-memory-changed"
const MAX_SUMMARY_MEMORY_CHARACTERS = 2000
const TRUNCATION_SUFFIX = "... [truncated]"

export const SUMMARY_MEMORY_API_PATH = "/api/sofia/summary-preferences"

const EMPTY_SELECTORS: CoviSummaryPreferenceSelectors = {
  agents: [],
  metadataKeys: [],
  metrics: [],
  personas: [],
  tags: [],
  terms: [],
  testSets: [],
}

export const EMPTY_SUMMARY_MEMORY: CoviSummaryMemory = {
  memoryEnabled: null,
  preferencesText: "",
  scope: "org",
  selectors: EMPTY_SELECTORS,
  updatedAt: null,
}

function storageKey(orgSlug: string): string {
  return `${SUMMARY_MEMORY_STORAGE_KEY_PREFIX}:${orgSlug}`
}

function legacyMonitoringFocusStorageKey(orgSlug: string): string {
  return `${LEGACY_MONITORING_FOCUS_STORAGE_KEY_PREFIX}:${orgSlug}`
}

function normalizePreferencesText(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

function boundSummaryMemoryText(value: string): string {
  const text = value.trim()
  return text.length > MAX_SUMMARY_MEMORY_CHARACTERS
    ? `${text
        .slice(0, MAX_SUMMARY_MEMORY_CHARACTERS - TRUNCATION_SUFFIX.length)
        .trimEnd()}${TRUNCATION_SUFFIX}`
    : text
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  const seen = new Set<string>()
  return value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter((item) => {
      const key = item.toLowerCase()
      if (!item || seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
}

function normalizeSelectors(value: unknown): CoviSummaryPreferenceSelectors {
  const record =
    typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : {}
  return {
    agents: normalizeStringArray(record.agents),
    metadataKeys: normalizeStringArray(
      record.metadataKeys ?? record.metadata_keys,
    ),
    metrics: normalizeStringArray(record.metrics),
    personas: normalizeStringArray(record.personas),
    tags: normalizeStringArray(record.tags),
    terms: normalizeStringArray(record.terms),
    testSets: normalizeStringArray(record.testSets ?? record.test_sets),
  }
}

function normalizeCoviSummaryMemory(value: unknown): CoviSummaryMemory {
  const record =
    typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : {}
  return {
    memoryEnabled:
      typeof record.memoryEnabled === "boolean"
        ? record.memoryEnabled
        : typeof record.memory_enabled === "boolean"
          ? record.memory_enabled
          : null,
    preferencesText: boundSummaryMemoryText(
      normalizePreferencesText(
        record.preferencesText ?? record.preferences_text,
      ),
    ),
    scope: "org",
    selectors: normalizeSelectors(record.selectors),
    updatedAt:
      typeof record.updatedAt === "string"
        ? record.updatedAt
        : typeof record.updated_at === "string"
          ? record.updated_at
          : null,
  }
}

function parseStoredSummaryMemory(value: string | null): CoviSummaryMemory {
  if (!value) {
    return EMPTY_SUMMARY_MEMORY
  }

  try {
    return normalizeCoviSummaryMemory(JSON.parse(value))
  } catch {
    return {
      ...EMPTY_SUMMARY_MEMORY,
      preferencesText: boundSummaryMemoryText(normalizePreferencesText(value)),
    }
  }
}

export function parseCoviSummaryMemoryResponse(
  payload: unknown,
): CoviSummaryMemory {
  const record =
    typeof payload === "object" && payload !== null
      ? (payload as Record<string, unknown>)
      : {}
  return normalizeCoviSummaryMemory({
    ...(typeof record.summary_preferences === "object" &&
    record.summary_preferences !== null
      ? (record.summary_preferences as Record<string, unknown>)
      : {}),
    memory_enabled: record.memory_enabled,
  })
}

export function buildCoviSummaryPreferencesRequest(
  memory: CoviSummaryMemory,
): Record<string, unknown> {
  return {
    summary_preferences: {
      preferences_text: boundSummaryMemoryText(memory.preferencesText),
    },
  }
}

function dispatchSummaryMemoryChange(detail: CoviSummaryMemoryChangeDetail) {
  if (typeof window === "undefined") {
    return
  }

  window.dispatchEvent(
    new CustomEvent<CoviSummaryMemoryChangeDetail>(SUMMARY_MEMORY_EVENT, {
      detail,
    }),
  )
}

export function readCoviSummaryMemory(
  orgSlug: string | null | undefined,
): CoviSummaryMemory {
  if (!orgSlug || typeof window === "undefined") {
    return EMPTY_SUMMARY_MEMORY
  }

  try {
    const stored = window.localStorage.getItem(storageKey(orgSlug))
    const parsed = parseStoredSummaryMemory(stored)
    if (parsed.preferencesText) {
      return parsed
    }

    return {
      ...EMPTY_SUMMARY_MEMORY,
      preferencesText: normalizePreferencesText(
        window.localStorage.getItem(legacyMonitoringFocusStorageKey(orgSlug)),
      ),
    }
  } catch {
    return EMPTY_SUMMARY_MEMORY
  }
}

export function writeCoviSummaryMemory(
  orgSlug: string | null | undefined,
  memory: Partial<CoviSummaryMemory> & { preferencesText: string },
): CoviSummaryMemory {
  if (!orgSlug || typeof window === "undefined") {
    return EMPTY_SUMMARY_MEMORY
  }

  const nextMemory = normalizeCoviSummaryMemory(memory)

  try {
    if (nextMemory.preferencesText) {
      window.localStorage.setItem(
        storageKey(orgSlug),
        JSON.stringify(nextMemory),
      )
    } else {
      window.localStorage.removeItem(storageKey(orgSlug))
    }
    window.localStorage.removeItem(legacyMonitoringFocusStorageKey(orgSlug))
  } catch {
    // localStorage can fail in private browsing or quota exhaustion. Dispatch the
    // in-memory update anyway so the current page session reflects the edit.
  }

  dispatchSummaryMemoryChange({ orgSlug, memory: nextMemory })
  return nextMemory
}

export function requestCoviSummaryMemoryRefresh(
  orgSlug: string | null | undefined,
): void {
  dispatchSummaryMemoryChange({ orgSlug: orgSlug ?? undefined, refresh: true })
}

export function subscribeCoviSummaryMemory(
  listener: (detail?: CoviSummaryMemoryChangeDetail) => void,
): () => void {
  if (typeof window === "undefined") {
    return () => {}
  }

  const onMemoryChange = (event: Event) => {
    listener((event as CustomEvent<CoviSummaryMemoryChangeDetail>).detail)
  }
  const onStorage = (event: StorageEvent) => {
    if (
      event.key?.startsWith(SUMMARY_MEMORY_STORAGE_KEY_PREFIX) ||
      event.key?.startsWith(LEGACY_MONITORING_FOCUS_STORAGE_KEY_PREFIX)
    ) {
      listener()
    }
  }

  window.addEventListener(SUMMARY_MEMORY_EVENT, onMemoryChange)
  window.addEventListener("storage", onStorage)

  return () => {
    window.removeEventListener(SUMMARY_MEMORY_EVENT, onMemoryChange)
    window.removeEventListener("storage", onStorage)
  }
}
